import React, { useMemo, useCallback } from 'react';
import { DataGrid } from '../ui/DataGrid';
import { GridColumn, GridToolbarFilter, GridView, RowAction } from '../../types/datagrid';
import { Project, ProjectListParams } from '../../types';

interface ProjectsDataGridProps {
  projects: Project[];
  loading?: boolean;
  error?: string;
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  onFilter?: (filters: ProjectListParams) => void;
  onProjectUpdate?: (projectId: string, updates: Partial<Project>) => Promise<void>;
  onProjectSelect?: (projectId: string) => void;
  onProjectEdit?: (projectId: string) => void;
  onProjectDelete?: (projectId: string) => void;
  onProjectDuplicate?: (projectId: string) => void;
  currentProjectId?: string;
  permissions?: {
    canEdit?: boolean;
    canDelete?: boolean;
    canCreateView?: boolean;
    canExport?: boolean;
  };
}

export const ProjectsDataGrid: React.FC<ProjectsDataGridProps> = ({
  projects,
  loading = false,
  error,
  totalCount = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFilter,
  onProjectUpdate,
  onProjectSelect,
  onProjectEdit,
  onProjectDelete,
  onProjectDuplicate,
  permissions = {},
}) => {
  // Define row actions
  const rowActions = useMemo(
    (): RowAction[] => [
      {
        id: 'view',
        label: 'View Project',
        variant: 'default',
      },
      {
        id: 'edit',
        label: 'Edit Project',
        variant: 'default',
        isVisible: () => permissions.canEdit !== false,
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        variant: 'default',
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'destructive',
        isVisible: () => permissions.canDelete !== false,
        isDisabled: row => row.status === 'active',
      },
    ],
    [permissions.canEdit, permissions.canDelete]
  );

  // Handle row action
  const handleRowAction = useCallback(
    (rowId: string, action: string) => {
      switch (action) {
        case 'view':
          onProjectSelect?.(rowId);
          break;
        case 'edit':
          onProjectEdit?.(rowId);
          break;
        case 'delete':
          onProjectDelete?.(rowId);
          break;
        case 'duplicate':
          onProjectDuplicate?.(rowId);
          break;
      }
    },
    [onProjectSelect, onProjectEdit, onProjectDelete, onProjectDuplicate]
  );
  // Define column structure for projects
  const columns = useMemo(
    (): GridColumn[] => [
      {
        id: 'projectNumber',
        key: 'projectNumber',
        label: 'Project Number',
        originalLabel: 'Project Number',
        type: 'link',
        width: 140,
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
        isSortable: true,
        isHideable: false, // Project number should always be visible for navigation
        isRenameable: false,
        isEditable: true, // Allow editing during project setup
        isPinned: 'left',
        isVisible: true,
        isPrivate: true, // Private column (can't be hidden by non-admins)
        isLocked: false,
        order: 0,
        validation: {
          required: true,
          message: 'Project number is required',
        },
      },
      {
        id: 'name',
        key: 'name',
        label: 'Name',
        originalLabel: 'Name',
        type: 'text',
        width: 200,
        minWidth: 150,
        isResizable: true,
        isSortable: true,
        isHideable: false,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 1,
        validation: {
          required: true,
          message: 'Project name is required',
        },
      },
      {
        id: 'client',
        key: 'client',
        label: 'Client',
        originalLabel: 'Client',
        type: 'text',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 2,
      },
      {
        id: 'projectType',
        key: 'projectType',
        label: 'Project type',
        originalLabel: 'Project type',
        type: 'text',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 3,
      },
      {
        id: 'startDate',
        key: 'startDate',
        label: 'Start date',
        originalLabel: 'Start date',
        type: 'date',
        width: 110,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 4,
      },
      {
        id: 'endDate',
        key: 'endDate',
        label: 'End date',
        originalLabel: 'End date',
        type: 'date',
        width: 110,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 5,
      },
      {
        id: 'projectStatus',
        key: 'status',
        label: 'Project status',
        originalLabel: 'Project status',
        type: 'status',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 6,
        options: [
          { label: 'Planning', value: 'planning', color: '#8B5CF6' },
          { label: 'Active', value: 'active', color: '#10B981' },
          { label: 'On Hold', value: 'on_hold', color: '#F59E0B' },
          { label: 'Completed', value: 'completed', color: '#3B82F6' },
          { label: 'Cancelled', value: 'cancelled', color: '#EF4444' },
        ],
      },
      {
        id: 'revisedContract',
        key: 'revisedContract',
        label: 'Revised Contract',
        originalLabel: 'Revised Contract',
        type: 'currency',
        width: 140,
        minWidth: 120,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 7,
      },
      {
        id: 'actualCosts',
        key: 'actualCosts',
        label: 'Actual Costs',
        originalLabel: 'Actual Costs',
        type: 'currency',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: false, // Calculated field, not directly editable
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 8,
      },
      {
        id: 'paidInvoices',
        key: 'paidInvoices',
        label: 'Paid Invoices',
        originalLabel: 'Paid Invoices',
        type: 'currency',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: false,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 9,
      },
      {
        id: 'grossProfit',
        key: 'grossProfit',
        label: 'Gross Profit (Actual)',
        originalLabel: 'Gross Profit (Actual)',
        type: 'calculated',
        width: 150,
        minWidth: 120,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: false,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 10,
        calculated: {
          id: 'grossProfit',
          formula: '{Paid Invoices} - {Actual Costs}',
          dependencies: ['paidInvoices', 'actualCosts'],
        },
      },
      {
        id: 'manager',
        key: 'manager',
        label: 'Manager',
        originalLabel: 'Manager',
        type: 'user',
        width: 120,
        minWidth: 100,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: true,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 11,
      },
      {
        id: 'attachments',
        key: 'attachments',
        label: 'Attachments',
        originalLabel: 'Attachments',
        type: 'number',
        width: 100,
        minWidth: 80,
        isResizable: true,
        isSortable: true,
        isHideable: true,
        isRenameable: false,
        isEditable: false,
        isPinned: false,
        isVisible: true,
        isPrivate: false,
        isLocked: false,
        order: 12,
      },
    ],
    []
  );

  // Define toolbar filters
  const toolbarFilters = useMemo(
    (): GridToolbarFilter[] => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        placeholder: 'Search projects...',
      },
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'All Statuses', value: '' },
          { label: 'Planning', value: 'planning' },
          { label: 'Active', value: 'active' },
          { label: 'On Hold', value: 'on_hold' },
          { label: 'Completed', value: 'completed' },
          { label: 'Cancelled', value: 'cancelled' },
        ],
      },
      {
        id: 'priority',
        label: 'Priority',
        type: 'select',
        options: [
          { label: 'All Priorities', value: '' },
          { label: 'Low', value: 'low' },
          { label: 'Medium', value: 'medium' },
          { label: 'High', value: 'high' },
          { label: 'Critical', value: 'critical' },
        ],
      },
      {
        id: 'dateRange',
        label: 'Date Range',
        type: 'daterange',
      },
    ],
    []
  );

  // Transform projects data for calculated columns
  const transformedProjects = useMemo(() => {
    return projects.map(project => {
      const extProject = project as any; // Cast to access optional extended properties
      return {
        ...project,
        // Ensure projectNumber exists, fallback to generating one from ID if needed
        projectNumber: project.projectNumber || `PRO-${project.id?.slice(-6).toUpperCase()}`,
        // Add project type field (can be derived from customFields or default)
        projectType: extProject.customFields?.projectType || 'Construction',
        // Calculate gross profit
        grossProfit: (extProject.paidInvoices || 0) - (extProject.actualCosts || 0),
        // Add revised contract field (can be derived from budget or separate field)
        revisedContract: extProject.revisedContract || project.budget || 0,
        // Count attachments
        attachments: extProject.attachments?.length || 0,
        // Ensure manager is properly formatted
        manager: project.manager || null,
      };
    });
  }, [projects]);

  // Handle sorting
  const handleSort = useCallback(
    (sortStates: any[]) => {
      if (sortStates.length > 0 && onSort) {
        const primarySort = sortStates[0];
        onSort(primarySort.field, primarySort.direction);
      }
    },
    [onSort]
  );

  // Handle filtering
  const handleFilter = useCallback(
    (filters: Record<string, any>) => {
      if (onFilter) {
        const params: Partial<ProjectListParams> = {};

        if (filters.search) params.search = filters.search;
        if (filters.status) params.status = filters.status;
        if (filters.priority) params.priority = filters.priority;

        if (filters.dateRange && Array.isArray(filters.dateRange)) {
          const [startDate, endDate] = filters.dateRange;
          if (startDate) params.dateFrom = startDate;
          if (endDate) params.dateTo = endDate;
        }

        onFilter(params as ProjectListParams);
      }
    },
    [onFilter]
  );

  // Handle cell editing
  const handleCellEdit = useCallback(
    async (rowId: string, columnId: string, value: any) => {
      if (onProjectUpdate) {
        const updates: Partial<Project> = { [columnId]: value };
        await onProjectUpdate(rowId, updates);
      }
    },
    [onProjectUpdate]
  );

  // Handle pagination
  const handlePaginationChange = useCallback(
    (page: number, limit: number) => {
      onPageChange?.(page);
      onPageSizeChange?.(limit);
    },
    [onPageChange, onPageSizeChange]
  );

  // Handle row click (navigate to project)
  const handleRowClick = useCallback(
    (row: any) => {
      if (onProjectSelect) {
        // Use the actual project ID for navigation, not the project number
        onProjectSelect(row.id || row._id);
      }
    },
    [onProjectSelect]
  );

  // Default view for projects
  const defaultView: GridView = useMemo(
    () => ({
      id: 'projects-default',
      name: 'Default Projects View',
      type: 'public',
      isDefault: true,
      description: 'Default view showing all essential project information',
      columns,
      filters: {},
      sorting: [{ field: 'createdAt', direction: 'desc', order: 1 }],
      pagination: {
        page: currentPage,
        limit: pageSize,
        total: totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    }),
    [columns, currentPage, pageSize, totalCount]
  );

  return (
    <DataGrid
      gridId="projects-grid"
      data={transformedProjects}
      columns={columns}
      loading={loading}
      error={error}
      defaultView={defaultView}
      toolbarFilters={toolbarFilters}
      enableSorting={true}
      enableFiltering={true}
      enableColumnReorder={true}
      enableColumnResize={true}
      enableInlineEdit={permissions.canEdit}
      enableRowSelection={false}
      enableVirtualization={true}
      enableExport={permissions.canExport}
      pagination={{
        page: currentPage,
        limit: pageSize,
        total: totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
      }}
      onPaginationChange={handlePaginationChange}
      onSort={handleSort}
      onFilter={handleFilter}
      onCellEdit={handleCellEdit}
      onRowClick={handleRowClick}
      rowActions={rowActions}
      onRowAction={handleRowAction}
      className="rounded-lg shadow-sm"
      rowHeight={44}
      headerHeight={48}
      stickyHeader={true}
      stickyToolbar={true}
      compactDensity={true}
      permissions={permissions}
    />
  );
};
